import { Text } from '@/components/ui/base/Text';
import { Accordion } from '@/components/Accordion';
import { AnalysisAccordion } from '../../common/AnalysisAccordion';
import type { FileDetection } from '../../../../../types/api.types';
import type { HandleToggleChangeProps } from '@/features/analysis/types/canvas.types';
import { Dispatch } from 'react';
import { SetStateAction } from 'react';
import {
  getDetectionOptions,
  getDetectionType,
  isDetectionClickable,
} from '@/utils/utils';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface DetectionAccordionProps {
  page: number;
  detections: FileDetection[];
  isOpen: boolean;
  onToggle: () => void;
  setPageIndex: (index: number) => void;
  setHoverDetection: Dispatch<SetStateAction<string | undefined>>;
  hoverDetection?: string;
  pageIndex: number;
  handleToggleChange: HandleToggleChangeProps;
  hideDetectionCanvas?: boolean;
  selectedMrzId?: string;
}

export const DetectionAccordion = ({
  page,
  detections,
  isOpen,
  onToggle,
  setPageIndex,
  setHoverDetection,
  hoverDetection,
  pageIndex,
  handleToggleChange,
  hideDetectionCanvas,
  selectedMrzId,
}: DetectionAccordionProps) => {
  return (
    <Accordion
      header={
        <>
          <Text weight="normal">
            {page === -1 ? 'Non localisables' : `Page ${page + 1}`}
          </Text>
          {page !== -1 && (
            <Button
              variant="secondary"
              className="ml-auto"
              endIcon={<ArrowRight size={16} />}
              width="fit"
              size="sm"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setPageIndex(page);
              }}
              type="button"
            >
              Voir
            </Button>
          )}
        </>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      chevronPosition="left"
      bg="neutralBG2"
      disableHover={page === -1}
    >
      <div className="rounded-b-md bg-opacity-100">
        {detections.map((detection) => {
          const detectionType = getDetectionType(detection);
          const isClickable = isDetectionClickable(detection);

          return (
            <AnalysisAccordion
              key={detection.id}
              setPageIndex={setPageIndex}
              setHoverDetection={setHoverDetection}
              hoverDetection={hoverDetection}
              selectedDetectionId={selectedMrzId}
              detectionId={detection.id}
              variant="detection"
              title={detection.title}
              content={detection.description}
              handleToggleChange={handleToggleChange}
              onMouseEnter={() => {
                if (!detection.location) return;
                const loc = detection.page === pageIndex;
                if (loc) {
                  setHoverDetection(detection.id);
                } else {
                  setHoverDetection(undefined);
                }
              }}
              isGoodDetection={detection.is_good}
              onMouseLeave={() => setHoverDetection(undefined)}
              tabLinks={detectionType && isClickable ? [detectionType] : []}
              disableHover={detection.page === null}
              hideDetectionCanvas={hideDetectionCanvas}
              locations={detection.location ?? []}
              options={getDetectionOptions(detection)}
            />
          );
        })}
      </div>
    </Accordion>
  );
};
